import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BotaoDuplo from '../../../Componentes/BotaoDuplo';
import CampoEdicoes, { InputCampoEdicoes } from '../../../Componentes/CampoEdicoes';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import SelectInput from '../../../Componentes/SelectInput';
import TextInput from '../../../Componentes/TextInput';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import { apiService } from '../../../Services/ApiService';
import './UsuarioCadastro.css';
import roles from '../../../constants/roles';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export default function UsuarioCadastro({ myData = false }) {
  const { t } = useTranslation('errors');
  const { t: tRoles } = useTranslation('roles');
  const [user, setUser] = useState({
    name: '',
    password: '',
    control: '',
    email: '',
    role: '',
    receiveEmails: true,
    phone: '',
  });

  const [newPassword, setNewPassword] = useState({
    oldPassword: '',
    newPassword: '',
    isValid: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    name: true,
    email: false,
    control: true,
    password: true,
    phone: false,
    role: true,
  });

  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    control: false,
    password: false,
    phone: false,
    role: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });

  const [modalCancel, setModalCancel] = useState(false);

  const navega = useNavigate();
  const { id } = useParams();
  const { get, post, put } = apiService();

  const userRoles = Object.keys(roles).map((role) => ({
    value: role,
    label: tRoles(role.toString()),
  }));

  useEffect(() => {
    if (id) {
      getUserById();
      setIsEditing(true);
    } else if (myData) {
      getUserById();
    } else {
      setIsLoading(false);
      setIsEditing(false);
    }
  }, [id, myData]);

  useEffect(() => {
    if (myData) {
      validatePasswordChange();
    }
  }, [newPassword.newPassword, newPassword.oldPassword]);

  useEffect(() => {
    setIsFormValid(Object.values(validationErrors).every((v) => !v));
  }, [validationErrors]);

  useEffect(() => {
    if (isEditing) {
      setTouchedFields((prev) => ({ ...prev, password: true }));
      setValidationErrors((prev) => ({ ...prev, password: false }));
    }
  }, [isEditing]);

  const getUserById = async () => {
    try {
      setIsLoading(true);
      let user;

      if (id) {
        user = await get(`users/${id}`);
      } else if (myData) {
        user = await get(`users/me`);
      }

      if (user) {
        setUser({
          name: user.name,
          email: user.email,
          control: user.control,
          role: user.role,
          receiveEmails: user.emailEnabled,
          phone: user.phone,
        });

        Object.entries(user).forEach(([key, value]) => {
          setAndValidateField(key, value);
          setTouchedFields((prev) => ({ ...prev, [key]: true }));
        });
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar usuário' });
    } finally {
      setIsLoading(false);
    }
  };

  const validators = {
    name: (value) => value && value.trim().length >= 3 && value.trim().length <= 100,
    email: (value) =>
      (value &&
        value.trim().length >= 3 &&
        value.trim().length <= 50 &&
        /\S+@\S+\.\S+/.test(value)) ||
      value === null ||
      value === '',
    control: (value) => value && /^\d{7,15}$/.test(value.trim()),
    password: (value) =>
      isEditing
        ? !value || (value && value.length >= 8 && value.length <= 64)
        : value && value.length >= 8 && value.length <= 64,
    phone: (value) => {
      if (!value) return true;
      try {
        const phone = parsePhoneNumberFromString(value);
        return phone && phone.isValid() && phone.number.length >= 14 && phone.number.length <= 15;
      } catch (error) {
        return false;
      }
    },
    role: (value) => value && value !== '',
  };

  const validatePassword = (value) => {
    return value && value.length >= 8 && value.length <= 64;
  };

  const validateField = (name, value) => {
    return validators[name] ? validators[name](value) : true;
  };

  const setAndValidateField = (name, value) => {
    if (name === 'phone' && value) {
      // Remove all non-numeric characters, except the '+' sign
      value = value.replace(/[^0-9+]/g, '');
    }
    setUser((prev) => ({ ...prev, [name]: value }));
    const isValid = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: !isValid }));
  };

  const handleChange = (name, value) => {
    setAndValidateField(name, value);
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const handleRoleChange = ({ value, label }) => {
    handleChange('role', value);
  };

  const handlePasswordChange = (name, value) => {
    setNewPassword((prev) => ({ ...prev, [name]: value }));
  };

  const validatePasswordChange = () => {
    const newIsValid = validatePassword(newPassword.newPassword);
    const oldIsValid = validatePassword(user.password);

    if (newIsValid && oldIsValid) {
      setNewPassword((prev) => ({ ...prev, isValid: true }));
    } else {
      setNewPassword((prev) => ({ ...prev, isValid: false }));
    }
  };

  const handleSave = async () => {
    if (!isFormValid) {
      setModalConfirm(false);
      return;
    }
    setModalConfirm(true);
  };

  const saveUser = async () => {
    if (!isFormValid) {
      setModalConfirm(false);
      return;
    }

    if (!myData) {
      const data = {
        id,
        control: user.control,
        phone:
          user.phone && user.phone.trim().length
            ? parsePhoneNumberFromString(user.phone).number
            : null,
        email: user.email && user.email.trim().length ? user.email : null,
        name: user.name,
        role: user.role,
        emailEnabled: user.receiveEmails,
      };

      if (user.password && touchedFields.password && !validationErrors.password) {
        data.password = user.password;
      }

      try {
        setIsLoading(true);
        if (id) {
          await put(`users`, data);
        } else {
          await post('users', data);
        }

        setModalSuccess(true);
      } catch (error) {
        let errorMessage = 'Erro ao salvar usuário';
        if (error.message) {
          errorMessage = t(error.message);
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = t(`errors.${error.response.data.message}`);
        }

        setModalError({
          visible: true,
          message: errorMessage,
        });
      } finally {
        setModalConfirm(false);
        setIsLoading(false);
      }
    } else {
      const data = {
        currentPassword: user.password,
        newPassword: newPassword.newPassword,
      };

      try {
        setIsLoading(true);
        await put(`users/update-password`, data);
        setModalSuccess(true);
      } catch (error) {
        if (error.message && t(error.message)) {
          setModalError({
            visible: true,
            message: t(error.message),
          });
        } else {
          setModalError({
            visible: true,
            message: 'Erro ao mudar a senha',
          });
        }
      } finally {
        setModalConfirm(false);
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setModalCancel(true);
  };

  return (
    <CorpoPagina loading={isLoading}>
      {modalCancel && (
        <ModalConfirmacao
          aviso="Deseja realmente cancelar?"
          confirmar={false}
          clicado={() => navega(-1)}
          clicadoVolta={() => setModalCancel(false)}
        />
      )}
      {modalSuccess && (
        <ModalConfirmacao
          aviso={!myData ? 'Usuário salvo com sucesso!' : 'Senha alterada com sucesso!'}
          confirmar={true}
          clicado={() => navega(-1)}
        />
      )}
      {modalConfirm && (
        <ModalConfirmacao
          aviso={!myData ? 'Deseja realmente salvar o usuário?' : 'Deseja realmente mudar a senha?'}
          confirmar={false}
          clicado={() => saveUser()}
          clicadoVolta={() => setModalConfirm(false)}
        />
      )}
      {modalError.visible && (
        <ModalConfirmacao
          aviso={modalError.message}
          erro={true}
          clicado={() => setModalError({ visible: false, message: '' })}
        />
      )}
      <div className="ContainerGeral">
        {!isEditing && <TopicoTexto titulo="Cadastrar Usuário" marginLeft={15} />}
        {isEditing && <TopicoTexto titulo="Editar Usuário" marginLeft={15} />}
        <div className="MeioLinha">
          <div className="form-column">
            <CampoEdicoes
              titulo="DADOS DO USUÁRIO"
              maxH={'100%'}
              maxW={540}
              width={'100%'}
              height={'100%'}
              left={16}
              right={16}
            >
              <TextInput
                titulo="Nome"
                placeholder="Insira o Nome"
                tipo="input"
                padrao={user.name}
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('name', txt)}
                erro={touchedFields.name && validationErrors.name}
                erroTexto="Nome inválido"
                disabled={!!myData}
                maxLength={100}
              />
              <TextInput
                titulo="E-mail"
                placeholder="Insira o E-mail"
                tipo="email"
                padrao={user.email}
                largura="90%"
                onUpdate={(txt) => handleChange('email', txt)}
                erro={touchedFields.email && validationErrors.email}
                erroTexto="E-mail inválido"
                disabled={!!myData}
                maxLength={50}
              />
              <div
                style={{
                  width: '90%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <TextInput
                  titulo="Controle"
                  placeholder="Insira o controle"
                  padrao={user.control}
                  tipo="phone"
                  largura="45%"
                  onUpdate={(txt) => handleChange('control', txt)}
                  erro={touchedFields.control && validationErrors.control}
                  erroTexto="Controle inválido"
                  disabled={
                    (isEditing && user.control === '0000001' && user.role === 'OWNER') || !!myData
                  }
                  maxLength={15}
                />
                <TextInput
                  titulo="Telefone"
                  placeholder="+55929XXXXXXXX"
                  tipo="phone"
                  padrao={user.phone}
                  largura="45%"
                  onUpdate={(txt) => handleChange('phone', txt)}
                  erro={touchedFields.phone && validationErrors.phone}
                  erroTexto="Telefone inválido"
                  disabled={!!myData}
                  maxLength={15}
                />
              </div>
              <InputCampoEdicoes
                desc="Habilitar usuário para receber emails de alerta"
                isChecked={user.receiveEmails}
                top={16}
                clicado={(status) => handleChange('receiveEmails', status ? true : false)}
                disabled={!!myData}
              />
            </CampoEdicoes>
          </div>

          <div className="form-column">
            <CampoEdicoes
              titulo="PERFIL"
              maxH={'100%'}
              maxW={540}
              width={'100%'}
              left={16}
              right={16}
              height={'100%'}
            >
              <SelectInput
                titulo="Selecione o perfil"
                itens={userRoles}
                padrao={
                  user.role !== ''
                    ? { value: user.role, label: tRoles(user.role) }
                    : { value: '', label: 'Selecione' }
                }
                largura="90%"
                clicado={(txt) => handleRoleChange(txt)}
                disabled={
                  (isEditing && user.control === '0000001' && user.role === 'OWNER') || !!myData
                }
              />
            </CampoEdicoes>

            <CampoEdicoes
              titulo="DADOS DE ACESSO"
              maxH={'100%'}
              maxW={540}
              width={'100%'}
              left={16}
              right={16}
              height={'100%'}
              marginTop={20}
            >
              {!myData && (
                <>
                  <TextInput
                    titulo="Senha"
                    password
                    placeholder={isEditing ? '**********' : 'Insira a senha'}
                    tipo="password"
                    largura="90%"
                    top={8}
                    onUpdate={(txt) => handleChange('password', txt)}
                    padrao={user.password}
                    erro={touchedFields.password && validationErrors.password}
                    erroTexto="Senha inválida"
                    maxLength={64}
                  />
                  {isEditing && (
                    <p
                      style={{
                        textAlign: 'left',
                        color: 'rgb(47, 47, 47)',
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}
                    >
                      Senha definida. Você pode editar a senha se desejar.
                    </p>
                  )}
                </>
              )}
              {!!myData && (
                <>
                  <TextInput
                    titulo="Senha atual"
                    password
                    placeholder="**********"
                    tipo="password"
                    largura="90%"
                    top={8}
                    onUpdate={(txt) => handleChange('password', txt)}
                    padrao={user.password}
                    erro={touchedFields.password && validationErrors.password}
                    erroTexto="Senha inválida"
                    maxLength={64}
                  />
                  <TextInput
                    titulo="Nova senha"
                    password
                    placeholder="Nova senha"
                    tipo="password"
                    largura="90%"
                    top={8}
                    onUpdate={(txt) => handlePasswordChange('newPassword', txt)}
                    padrao={newPassword.newPassword}
                    erro={newPassword.isTouched && !newPassword.isValid}
                    erroTexto="Senha inválida"
                    maxLength={64}
                  />
                </>
              )}
              {!!myData && (
                <TextInput
                  titulo="Senha atual"
                  password
                  placeholder="Senha atual"
                  tipo="password"
                  largura="90%"
                  top={8}
                  onUpdate={(txt) => handleChange('password', txt)}
                  padrao={user.password}
                  erro={touchedFields.password && validationErrors.password}
                  erroTexto="Senha inválida"
                  maxLength={64}
                />
              )}
              {!!myData && (
                <TextInput
                  titulo="Nova senha"
                  password
                  placeholder="Nova senha"
                  tipo="password"
                  largura="90%"
                  top={8}
                  onUpdate={(txt) => handlePasswordChange('newPassword', txt)}
                  padrao={newPassword.newPassword}
                  erro={newPassword.isTouched && !newPassword.isValid}
                  erroTexto="Senha inválida"
                  maxLength={64}
                />
              )}
            </CampoEdicoes>
          </div>
        </div>

        {!myData && (
          <BotaoDuplo
            nome="Salvar"
            nomeVolta="Cancelar"
            top={32}
            bottom={32}
            clicado={handleSave}
            clicadoVolta={handleCancel}
            externo={true}
            disabled={!isFormValid}
          />
        )}
        {myData && (
          <BotaoDuplo
            nome="Salvar"
            nomeVolta="Cancelar"
            top={32}
            bottom={32}
            clicado={handleSave}
            clicadoVolta={handleCancel}
            externo={true}
            disabled={!isFormValid || !newPassword.isValid}
          />
        )}
      </div>
    </CorpoPagina>
  );
}
